import { equals, filter, map, path, pipe, reject, uniqBy } from 'ramda'

import type { AnnotationClasses, AnnotationValidation } from '../types'
import { getAnnotationStatus } from './observations'

type ProductClassesMixin = {
  productClasses: string[]
}

type AnnotationClassesMixin = {
  annotationClasses: AnnotationClasses
}

type AnnotationValidationMixin = {
  annotationValidation: AnnotationValidation
}

export const getClassesSummary = (
  observations: ProductClassesMixin[],
  t: (key: string) => string
) =>
  pipe(
    uniqBy(path(['productClasses'])),
    map((i: ProductClassesMixin) => {
      const isEmpty =
        !i.productClasses ||
        i.productClasses.length === 0 ||
        i.productClasses.every((cls) => cls.trim() === '')

      return {
        title: isEmpty ? t('Not annotated') : i.productClasses.join(', '),
        count: filter(
          (p) => equals(p.productClasses, i.productClasses),
          observations
        ).length,
      }
    })
  )(observations)

export const unique = (values: (string | undefined)[] | undefined): boolean => {
  const lowerCaseValues = values
    ? values.map((p) => p && p.trim().toLowerCase())
    : []

  const duplicates = lowerCaseValues.filter(
    (
      value: string | undefined,
      index: number,
      self: (string | undefined)[]
    ): boolean => self.indexOf(value) !== index
  )

  return duplicates.length === 0
}

export const containsEmptyStrings = (productClasses: string[]): number[] =>
  reject(
    (i) => i === 'toReject',
    productClasses.map((c, i) => (c.trim() === '' ? i : 'toReject'))
  ) as number[]

export const countValidAnnotationObservations = (
  observations: (AnnotationValidationMixin & AnnotationClassesMixin)[]
) =>
  observations.filter(
    (o) =>
      o.annotationValidation === 'VALID' &&
      o.annotationClasses !== 'DEFINED_EMPTY'
  ).length

export const countNotAnnotatedObservations = (
  observations: AnnotationClassesMixin[]
) => observations.filter((o) => o.annotationClasses === 'UNDEFINED').length

export const countEmptyAnnotationObservations = (
  observations: AnnotationClassesMixin[]
) => observations.filter((o) => o.annotationClasses === 'DEFINED_EMPTY').length

export const countInvalidAnnotationObservations = (
  observations: {
    annotationClasses: AnnotationClasses
    annotationValidation: AnnotationValidation
  }[]
) =>
  observations.filter(
    (o) =>
      getAnnotationStatus(o.annotationValidation, o.annotationClasses) ===
      'INVALID'
  ).length

export const countAnomalyObservations = (
  observations: { productClasses?: string[] }[]
) =>
  observations.filter((o) =>
    o.productClasses?.some((cls) => cls.toLowerCase().includes('anomaly'))
  ).length

export const countNormalObservations = (
  observations: { productClasses?: string[] }[]
) =>
  observations.filter((o) =>
    o.productClasses?.some((cls) => cls.toLowerCase().includes('normal'))
  ).length
